/**
 * O aceite do kickoff — o "li e está de acordo" do consultor, com escopo declarado.
 *
 * **Aceitar exige todas as trilhas enviadas.** O aceite inicia o prazo contratual quando o
 * ambiente também está liberado. Aceite parcial deixaria a entrega prevista correr enquanto o
 * cliente ainda responde.
 *
 * **Com a K-O no GeoTask, o aceite é o passo 1** ("Aceitar respostas"): a K-O é gerada depois,
 * no cartão dos atos. Pelo Jira, o rótulo é o de sempre. Enquanto o caminho não chega (`null`),
 * o botão espera desabilitado, sem prometer nenhum dos dois rótulos.
 *
 * **O que falta é dito ANTES, no cartão.** A confirmação só abre depois do envio completo.
 */

import { useState } from "react";
import { Aviso, Botao, CampoLongo, Cartao, Chip } from "../ui";
import { mensagemDoConsole, useAceitarKickoff } from "../../nucleo/console";
import { TRILHA_DE_INFRAESTRUTURA, type RevisaoDoKickoff } from "../../nucleo/kickoff";
import type { ModoDoKickoff } from "../../nucleo/marcosDoKickoff";
import { MAXIMO_DA_PENDENCIA } from "./pendencia";

export interface PropsDoAceite {
  revisao: RevisaoDoKickoff;
  /** Quem gera a K-O desta implantação; sem ele, vale o Jira; `null` enquanto carrega. */
  modo?: ModoDoKickoff | null;
}

export function AceiteDoKickoff({ revisao, modo = "jira" }: PropsDoAceite) {
  const [confirmando, setConfirmando] = useState(false);
  const [observacao, setObservacao] = useState("");
  const aceite = useAceitarKickoff(revisao.implantacao_id);

  /* A infraestrutura fica fora, como no servidor (`revisao._trilhas_do_kickoff`): ela tem ato
     próprio, do time de infra. Contá-la aqui fazia a confirmação anunciar "3 trilhas ainda não
     foram enviadas" e o aceite gravado registrar 2 — a tela prometia um registro que não fazia. */
  const pendentes = revisao.trilhas.filter(
    (trilha) => trilha.chave !== TRILHA_DE_INFRAESTRUTURA && trilha.sessao.estado !== "enviada",
  );
  /* O aceite recém-registrado, OU o que a revisão trouxe do servidor. A ordem importa: logo
     depois do clique a mutação é a fonte mais nova; em qualquer outra abertura da tela — inclusive
     a de outro consultor — quem responde é `revisao.aceite`.

     Antes daqui só existia `aceite.data`, e ele morre com o componente: bastava um `F5` para o
     cartão voltar a oferecer "aceitar" como se nada tivesse acontecido, e um segundo clique
     gravava um segundo evento na esteira do cliente. */
  const aceito = aceite.data ?? revisao.aceite;

  if (aceito && aceito.trilhas_pendentes.length === 0) {
    const enviadas = aceito.trilhas_enviadas.length;
    /* `pendencias_resolvidas` só existe na resposta da mutação: é o efeito colateral daquele
       clique, não um fato do aceite. Relido do servidor, o cartão simplesmente não o afirma —
       melhor calar do que inventar um número. */
    const resolvidas =
      "pendencias_resolvidas" in aceito ? aceito.pendencias_resolvidas.length : 0;
    /* Idem ao contrário: a observação só volta na LEITURA. A resposta da mutação não a repete
       — quem acabou de digitá-la está olhando para o próprio texto. */
    const observacaoRegistrada = "observacao" in aceito ? aceito.observacao : null;
    return (
      <Cartao
        titulo="Kickoff aceito"
        className="mb-4"
        data-testid="cartao-do-aceite"
        acessorio={<Chip estado="ok">aceito</Chip>}
      >
        <Aviso tom="sucesso" data-testid="kickoff-aceito">
          <b>Aceite registrado na linha do tempo por {aceito.aceito_por}.</b>{" "}
          {enviadas === 1 ? "1 trilha enviada" : `${enviadas} trilhas enviadas`};{" "}
          nenhuma trilha pendente.{" "}
          {resolvidas > 0
            ? `Fechou ${resolvidas} pendência${
                resolvidas === 1 ? "" : "s"
              } que a revisão tinha aberto.`
            : null}
        </Aviso>
        {observacaoRegistrada ? (
          <p className="cartao__texto mt-3" data-testid="observacao-registrada">
            <b>Observação registrada:</b> {observacaoRegistrada}
          </p>
        ) : null}
        <p className="nota-rodape mt-3">
          Gere a tarefa de kickoff e depois registre sua conclusão, nesta mesma
          revisão.
        </p>
      </Cartao>
    );
  }

  return (
    <Cartao
      titulo="Aceitar as respostas do kickoff"
      className={confirmando ? "cartao--grave mb-4" : "mb-4"}
      data-testid="cartao-do-aceite"
      acessorio={
        <Chip estado={pendentes.length > 0 ? "atencao" : "ok"}>
          {revisao.enviadas} de {revisao.total} enviadas
        </Chip>
      }
    >
      {!confirmando ? (
        <>
          {aceito?.trilhas_pendentes.length ? (
            <Aviso tom="atencao" className="mb-3" data-testid="aceite-legado-parcial">
              O aceite anterior foi registrado com trilhas pendentes.
              Depois que o cliente enviar todas elas, registre um novo aceite.
            </Aviso>
          ) : null}
          <p className="cartao__texto mb-3">
            O aceite entra na linha do tempo com o seu nome, fecha as pendências desta revisão
            e confirma a revisão das respostas. Gerar a tarefa e concluir o kickoff são os próximos atos.
          </p>
          {pendentes.length > 0 ? (
            <Aviso tom="atencao" className="mb-3" data-testid="aceite-com-pendencia">
              Antes do aceite, o cliente precisa enviar {pendentes.length === 1 ? "a trilha" : "as trilhas"}:{" "}
              {pendentes.map((item) => item.nome).join(", ")}.
            </Aviso>
          ) : null}
          {aceite.isError ? (
            <Aviso tom="erro" className="mb-3" data-testid="erro-do-aceite">
              {mensagemDoConsole(aceite.error)}
            </Aviso>
          ) : null}
          <div className="acoes-do-console">
            <Botao
              data-testid="aceitar-kickoff"
              disabled={pendentes.length > 0 || modo === null}
              aria-busy={modo === null || undefined}
              onClick={() => {
                aceite.reset();
                setConfirmando(true);
              }}
            >
              {modo === null ? "Aceitar" : modo === "geotask" ? "Aceitar respostas" : "Aceitar o kickoff"}
            </Botao>
          </div>
        </>
      ) : (
        <div data-testid="confirmacao-do-aceite">
          <p className="cartao__texto" role="status">
            <b>
              Aceitar o kickoff de{" "}
              {revisao.organizacao.nome_fantasia ?? revisao.organizacao.razao_social}?
            </b>{" "}
            O registro entra na esteira do cliente com o seu nome e a hora.
          </p>

          {revisao.contradicoes.length > 0 ? (
            <Aviso tom="atencao" className="mb-3" data-testid="aceite-com-contradicao">
              <b>
                {revisao.contradicoes.length === 1
                  ? "1 divergência entre trilhas continua em aberto."
                  : `${revisao.contradicoes.length} divergências entre trilhas continuam em aberto.`}
              </b>{" "}
              É um aviso, não um impedimento: a divergência pode ser legítima (um módulo pode
              funcionar de um jeito e outro de outro), e o aceite continua disponível. Aceitar não
              as resolve, e o portal não escolhe qual valor vale — confira antes de fechar.
            </Aviso>
          ) : null}

          {/* O texto entra em `implantacao_evento`, e `GET /implantacoes/{id}/eventos` exige só
              `esteira:ver` — que `admin_cliente`, `key_user` e `ti` têm. Ou seja: **o cliente lê
              esta observação**. A ajuda anterior sugeria o contrário ao dar como exemplo "o que
              foi combinado por telefone", que é justamente o tipo de nota que se escreve achando
              que é interna. Quem escreve precisa saber onde o texto vai parar. */}
          <CampoLongo
            rotulo="Observação (opcional)"
            rows={3}
            maxLength={MAXIMO_DA_PENDENCIA}
            value={observacao}
            onChange={(evento) => setObservacao(evento.target.value)}
            ajuda="O CLIENTE lê esta observação na linha do tempo dele, junto do seu nome. Escreva o que pode ser lido por ele — anotação interna não é aqui."
            data-testid="observacao-do-aceite"
          />

          {aceite.isError ? (
            <Aviso tom="erro" className="mb-3" data-testid="erro-do-aceite">
              {mensagemDoConsole(aceite.error)}
            </Aviso>
          ) : null}

          <div className="acoes-do-console">
            <Botao
              data-testid="confirmar-aceite"
              disabled={aceite.isPending}
              onClick={() => aceite.mutate({ observacao: observacao.trim() || null })}
            >
              {aceite.isPending ? "Registrando…" : "Confirmar o aceite"}
            </Botao>
            <Botao
              variante="fantasma"
              data-testid="cancelar-aceite"
              disabled={aceite.isPending}
              onClick={() => {
                aceite.reset();
                setConfirmando(false);
              }}
            >
              Cancelar
            </Botao>
          </div>
        </div>
      )}
    </Cartao>
  );
}
